import React, { useState, useEffect } from 'react';
import { Modal, Table, Pagination, message } from 'antd';
import { useQuery } from '@apollo/client';
import { t } from '../../../../i18n';
import {
    GET_ACCOUNT_DELIVERY_DRIVER_OPERATION_LOGS,
    GET_ACCOUNT_DELIVERY_DRIVER_OPERATION_LOGS_FOR_CENTRAL,
    GET_ACCOUNT_DELIVERY_DRIVER_OPERATION_LOGS_FOR_WMS,
    GetAccountDeliveryDriverOperationLogsResponse,
    GetAccountDeliveryDriverOperationLogsVariables,
    SystemOperationLogType
} from '../graphql/queries/getAccountDeliveryDriverOperationLogs';
import { formatTimestampToDateTime } from 'utils/dateTimeUtils';
import type { TableColumnsType } from 'antd';
import { ACCOUNTS_PAGE_MODE, AccountsPageMode } from '../hooks/useAccountDeliveryDriverQuery';
import { countryCodeOptions } from 'constants/countryCodes';
import { useVehicleTypeOptions } from "../../../../hooks/useVehicleTypeOptions";

// 判断是否为国际化键格式
const isI18nKey = (str: string): boolean => {
    return str.startsWith('delivery.') || str.startsWith('common.');
};

// 解析 JSON 模板格式的国际化键
const parseI18nTemplate = (str: string): { key: string; params: Record<string, string> } | null => {
    try {
        const parsed = JSON.parse(str);
        if (parsed.i18nKey && parsed.params) {
            return { key: parsed.i18nKey, params: parsed.params };
        }
    } catch {
        // 不是 JSON 格式
    }
    return null;
};

// 翻译 params 中的值（如果值是 i18n 键则翻译）
const translateParams = (params: Record<string, string>): Record<string, string> => {
    const result: Record<string, string> = {};
    for (const [key, value] of Object.entries(params)) {
        // 如果值是 i18n 键格式，则翻译
        if (isI18nKey(value)) {
            result[key] = t(value);
        } else {
            result[key] = value;
        }
    }
    return result;
};

// 翻译 description
const translateDescription = (description: string | null | undefined): string => {
    if (!description) return '-';
    
    // 尝试解析 JSON 模板
    const template = parseI18nTemplate(description);
    if (template) {
        // 翻译 params 中的值
        const translatedParams = translateParams(template.params);
        return t(template.key, translatedParams);
    }
    
    // 如果是简单的国际化键
    if (isI18nKey(description)) {
        return t(description);
    }
    
    // 兼容旧数据：直接返回原文
    return description;
};

interface OperationLogModalProps {
    visible: boolean;
    accountId: number | null;
    onClose: () => void;
    mode?: AccountsPageMode; // 页面入口，决定调用的接口
    warehouseId?: number | null; // 仓库入口的当前仓库ID
}

// 字段名到翻译键的映射（字段名对应数据库表字段名，使用蛇形命名）
const fieldNameToTranslationKey: Record<string, string> = {
    // Account 表字段
    'is_employed': 'delivery.personnel.label.isEmployed',
    'status': 'delivery.personnel.label.personnelStatus',
    'name': 'delivery.personnel.label.name',
    'email': 'delivery.personnel.label.email',
    'phone': 'delivery.personnel.label.phone',
    'country_code': 'delivery.personnel.label.phone',
    // AccountDeliveryDriver 表字段
    'is_on_duty': 'delivery.personnel.label.dutyStatus',
    'is_blacklist': 'delivery.driver.isBlacklist',
    'vehicle_type_id': 'delivery.personnel.label.vehicleType',
    'vehicle_plate': 'delivery.personnel.label.vehiclePlate',
    'vehicle_image_s3_keys_json': 'delivery.personnel.label.vehiclePhotos',
    'driver_license_number': 'delivery.personnel.label.driverLicense',
    'id_document_type': 'delivery.driver.idDocumentType',
    'id_document_number': 'delivery.driver.idDocumentNumber',
    'id_document_image_s3_keys_json': 'delivery.driver.idDocumentPhotos',
};

// 图片字段列表（修改时只显示"修改"，不显示具体值）
const imageFields = new Set([
    'vehicle_image_s3_keys_json',
    'id_document_image_s3_keys_json',
]);

// 获取国家码显示文本
const getCountryCodeLabel = (code: number | null | undefined): string => {
    if (code === null || code === undefined) {
        return '';
    }
    const option = countryCodeOptions.find(opt => opt.value === code);
    return option ? option.label : `+${code}`;
};

// 格式化字段值
const formatFieldValue = (fieldName: string, value: any, vehicleTypeOptions: Array<{ label: string; value: number }>): string => {
    if (value === null || value === undefined) {
        return '-';
    }
    
    // 根据字段名特殊处理（使用数据库表字段名，蛇形命名）
    if (fieldName === 'is_employed') {
        return value === true ? t('delivery.personnel.label.employed') : t('delivery.personnel.label.unemployed');
    }
    if (fieldName === 'status') {
        return value === 1 ? t('delivery.personnel.label.active') : t('delivery.personnel.label.inactive');
    }
    if (fieldName === 'is_on_duty') {
        return value === true ? t('delivery.personnel.label.onDuty') : t('delivery.personnel.label.offDuty');
    }
    if (fieldName === 'country_code') {
        return getCountryCodeLabel(value);
    }
    if (fieldName === 'phone') {
        return String(value);
    }
    if (fieldName === 'vehicle_type_id') {
        // 根据车辆类型ID查找对应的文本
        const vehicleType = vehicleTypeOptions.find(option => option.value == value);
        return vehicleType ? vehicleType.label : String(value);
    }
    
    // 默认处理
    if (typeof value === 'boolean') {
        return value ? t('common.yes') : t('common.no');
    }
    if (typeof value === 'object') {
        return JSON.stringify(value);
    }
    return String(value);
};

// 解析并格式化变更内容
const parseChanges = (changesJson: string | null, vehicleTypeOptions: Array<{ label: string; value: number }>): string[] => {
    if (!changesJson) {
        return [];
    }
    try {
        const changes = JSON.parse(changesJson) as Array<{ fieldName: string; oldValue: any; newValue: any }>;
        const result: string[] = [];
        const processedFields = new Set<string>();
        
        // 检查是否有 phone 或 country_code 的变更，需要合并显示
        const phoneChange = changes.find(c => c.fieldName === 'phone');
        const countryCodeChange = changes.find(c => c.fieldName === 'country_code');
        
        if (phoneChange || countryCodeChange) {
            // 合并显示电话和国家码
            // 获取旧的国家码和电话号
            let oldCountryCode: number | null = null;
            let newCountryCode: number | null = null;
            let oldPhone: string | null = null;
            let newPhone: string | null = null;
            
            if (countryCodeChange) {
                oldCountryCode = countryCodeChange.oldValue ?? null;
                newCountryCode = countryCodeChange.newValue ?? null;
            }
            
            if (phoneChange) {
                oldPhone = phoneChange.oldValue ? String(phoneChange.oldValue) : null;
                newPhone = phoneChange.newValue ? String(phoneChange.newValue) : null;
            }
            
            // 如果只修改了 countryCode，phone 的 oldValue 和 newValue 应该相同（从 phoneChange 获取，如果存在）
            // 如果只修改了 phone，countryCode 的 oldValue 和 newValue 应该相同（从 countryCodeChange 获取，如果存在）
            // 如果都不存在，说明只修改了其中一个，另一个保持不变，但我们无法知道其值，所以只显示变更的部分
            
            const oldCountryCodeLabel = oldCountryCode !== null ? getCountryCodeLabel(oldCountryCode) : '';
            const newCountryCodeLabel = newCountryCode !== null ? getCountryCodeLabel(newCountryCode) : '';
            
            // 构建显示文本：优先显示国家码+电话号，如果没有国家码则只显示电话号
            const oldDisplay = oldCountryCodeLabel && oldPhone 
                ? `${oldCountryCodeLabel} ${oldPhone}` 
                : (oldPhone || oldCountryCodeLabel || '-');
            const newDisplay = newCountryCodeLabel && newPhone 
                ? `${newCountryCodeLabel} ${newPhone}` 
                : (newPhone || newCountryCodeLabel || '-');
            
            result.push(`${t('delivery.personnel.label.phone')}: ${oldDisplay} → ${newDisplay}`);
            
            // 标记已处理
            processedFields.add('phone');
            processedFields.add('country_code');
        }
        
        // 处理其他字段
        changes.forEach(change => {
            if (!processedFields.has(change.fieldName)) {
                const fieldLabel = fieldNameToTranslationKey[change.fieldName] 
                    ? t(fieldNameToTranslationKey[change.fieldName])
                    : change.fieldName;
                
                // 如果是图片字段，只显示"修改"
                if (imageFields.has(change.fieldName)) {
                    result.push(`${fieldLabel}: ${t('delivery.personnel.label.operationLogModified')}`);
                } else {
                    const oldValue = formatFieldValue(change.fieldName, change.oldValue, vehicleTypeOptions);
                    const newValue = formatFieldValue(change.fieldName, change.newValue, vehicleTypeOptions);
                    result.push(`${fieldLabel}: ${oldValue} → ${newValue}`);
                }
            }
        });
        
        return result;
    } catch (e) {
        console.error('Failed to parse changesJson:', e);
        return [];
    }
};

export const OperationLogModal: React.FC<OperationLogModalProps> = ({
    visible,
    accountId,
    onClose,
    mode,
    warehouseId
}) => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const vehicleTypeOptions = useVehicleTypeOptions();

    const isWmsMode = mode === ACCOUNTS_PAGE_MODE.WMS;
    // 按入口各声明一个查询，仅当前入口的查询执行；mode 不是三种入口之一时不查询（与列表查询一致，不设默认入口）
    const buildQueryOptions = (queryMode: AccountsPageMode) => ({
        variables: {
            accountId: accountId!,
            ...(isWmsMode && warehouseId ? { warehouseId } : {}),
            pageRequest: {
                page: page,
                size: pageSize
            }
        },
        skip: !visible || !accountId || mode !== queryMode || (isWmsMode && !warehouseId),
        fetchPolicy: 'network-only' as const
    });
    const centralQuery = useQuery<GetAccountDeliveryDriverOperationLogsResponse, GetAccountDeliveryDriverOperationLogsVariables>(
        GET_ACCOUNT_DELIVERY_DRIVER_OPERATION_LOGS_FOR_CENTRAL, buildQueryOptions(ACCOUNTS_PAGE_MODE.CENTRAL));
    const wmsQuery = useQuery<GetAccountDeliveryDriverOperationLogsResponse, GetAccountDeliveryDriverOperationLogsVariables>(
        GET_ACCOUNT_DELIVERY_DRIVER_OPERATION_LOGS_FOR_WMS, buildQueryOptions(ACCOUNTS_PAGE_MODE.WMS));
    const dspQuery = useQuery<GetAccountDeliveryDriverOperationLogsResponse, GetAccountDeliveryDriverOperationLogsVariables>(
        GET_ACCOUNT_DELIVERY_DRIVER_OPERATION_LOGS, buildQueryOptions(ACCOUNTS_PAGE_MODE.DSP));
    const activeQuery = mode
        ? { [ACCOUNTS_PAGE_MODE.CENTRAL]: centralQuery, [ACCOUNTS_PAGE_MODE.WMS]: wmsQuery, [ACCOUNTS_PAGE_MODE.DSP]: dspQuery }[mode]
        : undefined;
    const data = activeQuery?.data;
    const loading = activeQuery?.loading ?? false;
    const error = activeQuery?.error;
    const refetch = activeQuery?.refetch;

    // 当弹窗打开时，重置页码为 1
    useEffect(() => {
        if (visible) {
            setPage(1);
        }
    }, [visible]);

    useEffect(() => {
        if (visible && accountId && refetch) {
            refetch({
                accountId: accountId,
                pageRequest: {
                    page: page,
                    size: pageSize
                }
            });
        }
    }, [visible, accountId, page, pageSize, refetch]);

    useEffect(() => {
        if (error) {
            message.error(t('delivery.personnel.message.loadOperationLogsFailed'));
        }
    }, [error]);

    const columns: TableColumnsType<SystemOperationLogType> = [
        {
            title: t('delivery.personnel.label.operationLogOperator'),
            dataIndex: 'operatorName',
            key: 'operatorName',
            width: 220,
            render: (text: string | null, record: SystemOperationLogType) => {
                if (record.operatorId != null) {
                    return `${text || ''}（${record.operatorId}）`;
                }
                return text || '-';
            },
        },
        {
            title: t('delivery.personnel.label.operationLogOperatedAt'),
            dataIndex: 'operatedAt',
            key: 'operatedAt',
            width: 180,
            render: (timestamp: number) => timestamp ? formatTimestampToDateTime(timestamp) : '-',
        },
        {
            title: t('delivery.personnel.label.operationLogChanges'),
            dataIndex: 'changesJson',
            key: 'changesJson',
                render: (changesJson: string | null, record: SystemOperationLogType) => {
                    const changes = parseChanges(changesJson, vehicleTypeOptions);
                    if (changes.length === 0) {
                        return translateDescription(record.description);
                    }
                    return (
                        <div>
                            {changes.map((change, index) => (
                                <div key={index}>{change}</div>
                            ))}
                        </div>
                    );
                },
        },
    ];

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
    };

    const handlePageSizeChange = (_: number, newPageSize: number) => {
        setPageSize(newPageSize);
        setPage(1); // 改变每页大小时重置到第一页
    };

    return (
        <Modal
            title={t('delivery.personnel.label.operationLog')}
            open={visible}
            onCancel={onClose}
            footer={null}
            width={1000}
            centered
            bodyStyle={{ maxHeight: '600px', padding: '16px', display: 'flex', flexDirection: 'column' }}
        >
            <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <Table
                    columns={columns}
                    dataSource={data?.getAccountDeliveryDriverOperationLogs.content || []}
                    loading={loading}
                    rowKey="id"
                    pagination={false}
                    scroll={{ y: 400 }}
                    size="small"
                />
                {data && (
                    <div className="mt-4 flex justify-between items-center" style={{ flexShrink: 0 }}>
                        <div className="text-gray-500">
                            {t('table.pagination.totalItems', { total: data.getAccountDeliveryDriverOperationLogs.totalElements })}
                        </div>
                        <Pagination
                            size="small"
                            current={page}
                            pageSize={pageSize}
                            total={data.getAccountDeliveryDriverOperationLogs.totalElements}
                            showSizeChanger
                            showQuickJumper
                            onChange={handlePageChange}
                            onShowSizeChange={handlePageSizeChange}
                        />
                    </div>
                )}
            </div>
        </Modal>
    );
};

